import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { DATE_INPUT_LANG } from "@/constants/dates";
import {
  useCreateFinanceRefund,
  useFinanceInvoices,
  useRefundablePayments,
} from "@/hooks/useInvoices";
import type { Invoice } from "@/interfaces/invoices";
import { RECEIPT_METHOD_LABEL } from "@/interfaces/invoices";
import { formatAmount } from "@/utils/number-format";

const todayIso = () => new Date().toISOString().slice(0, 10);
const AMOUNT_EPS = 0.005;

/**
 * Record a refund the client already made out of band (Stripe / bank), for bookkeeping. Pick an
 * invoice → a payment with refundable balance → amount (≤ the remaining refundable) → the real
 * refund date + reason. Recording it is inert — it never changes the invoice status or credits.
 */
export default function NewRefundDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateFinanceRefund();
  const [invInput, setInvInput] = useState("");
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [paymentId, setPaymentId] = useState<number | "">("");
  const [amount, setAmount] = useState("");
  const [refundDate, setRefundDate] = useState(todayIso());
  const [reason, setReason] = useState("");
  const [reference, setReference] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Cross-org invoice search (GFG). Debounce-free: React Query caches per search term.
  const { data: invData } = useFinanceInvoices({ search: invInput || undefined, limit: 20 });
  const invoiceOptions = invData?.data ?? [];

  // Only payments with remaining refundable balance (payment − net refunded > 0) are refundable.
  const payments = useRefundablePayments(invoice?.id ?? null).data ?? [];

  function reset() {
    setInvInput("");
    setInvoice(null);
    setPaymentId("");
    setAmount("");
    setRefundDate(todayIso());
    setReason("");
    setReference("");
    setError(null);
  }
  function handleClose() {
    reset();
    onClose();
  }

  function submit() {
    setError(null);
    if (!invoice) return setError("Select an invoice.");
    if (paymentId === "") return setError("Select the payment being refunded.");
    const payment = payments.find((p) => p.paymentReceiptId === paymentId);
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) return setError("Enter a refund amount greater than 0.");
    if (payment && amt > Number(payment.remaining) + AMOUNT_EPS) {
      return setError(`Refund can't exceed the remaining refundable (${payment.remaining}).`);
    }
    if (!refundDate) return setError("Pick the refund date.");
    if (!reason.trim()) return setError("Enter a reason.");
    create.mutate(
      {
        invoiceId: invoice.id,
        paymentReceiptId: Number(paymentId),
        amount: amt,
        refundDate,
        reason: reason.trim(),
        reference: reference.trim() || undefined,
      },
      { onSuccess: handleClose, onError: (e) => setError((e as Error).message) },
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Record a refund</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
          Record a refund made to the client out of band (Stripe / bank), for bookkeeping. This does
          not change the invoice status or credit balances.
        </Typography>

        <Autocomplete
          options={invoiceOptions}
          value={invoice}
          onChange={(_, v) => {
            setInvoice(v);
            setPaymentId("");
            setAmount("");
          }}
          onInputChange={(_, v) => setInvInput(v)}
          getOptionLabel={(i) => i.invoiceNumber ?? `#${i.id}`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderOption={(props, i) => {
            const { key, ...optionProps } = props as { key?: string } & Record<string, unknown>;
            return (
              <li key={i.id} {...optionProps}>
                <div>
                  <div>{i.invoiceNumber ?? `#${i.id}`}</div>
                  <div style={{ fontSize: 12, color: "var(--color-graphite)" }}>
                    {[i.organizationName, i.contactName].filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField {...params} label="Invoice" size="small" placeholder="Search number…" />
          )}
        />

        <TextField
          select
          label="Payment being refunded"
          size="small"
          value={paymentId}
          onChange={(e) => {
            const id = e.target.value === "" ? "" : Number(e.target.value);
            setPaymentId(id);
            const p = payments.find((x) => x.paymentReceiptId === id);
            setAmount(p ? String(Number(p.remaining)) : "");
          }}
          disabled={!invoice}
          helperText={
            invoice && payments.length === 0 ? "No payments with refundable balance." : " "
          }
        >
          {payments.map((p) => (
            <MenuItem key={p.paymentReceiptId} value={p.paymentReceiptId}>
              {`${p.currency.toUpperCase()} ${formatAmount(Number(p.remaining))} refundable`}
              {` — of ${formatAmount(Number(p.amount))}`}
              {`, ${p.receivedAt.slice(0, 10)}`}
              {p.method != null ? ` (${RECEIPT_METHOD_LABEL[p.method] ?? ""})` : ""}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Refund amount"
          size="small"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          disabled={paymentId === ""}
        />

        <TextField
          label="Refund date"
          size="small"
          type="date"
          lang={DATE_INPUT_LANG}
          value={refundDate}
          onChange={(e) => setRefundDate(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />

        <TextField
          label="Reason"
          size="small"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          multiline
          minRows={2}
        />

        <TextField
          label="Reference (optional)"
          size="small"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          helperText="e.g. the Stripe refund id"
        />

        {error && (
          <Typography sx={{ color: "var(--color-red, #c00)", fontSize: 13 }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={create.isPending}>
          {create.isPending ? "Saving…" : "Record refund"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
